import { API_ENDPOINT } from 'src/config/env.config';
import { baseApi } from './axios';
import {
	AcceptChangedTermsInput,
	ApplicationRoundType,
	ApplicationStatus,
	ApproveActionForApplicationInput,
	AssignBehaviouralFlagInput,
	BaseAPIResponse,
	BulkActionByRecruiterInput,
	CancelInterviewRoundInput,
	CreateApplicationInput,
	GetAllApplicationsAPIResponse,
	GetMaxNumberOfRoundsAPIResponse,
	GetMyApplicationsAPIResponse,
	GetOpenApplicationsAPIResponse,
	GetUsersListForARoundApiResponse,
	MoveUserToNextRoundAPIResponse,
	MoveUserToNextRoundInput,
	RejectActionForApplicationInput,
	RejectCandidateInput,
	ScreenRejectCandidateInput,
	RequestForOptOutInput,
	RespondToOfferLetterInput,
	ScheduleInterviewRoundInput,
	SendAssignmentToUserInput,
	SendMessageToJobseekerInput,
	SendOfferLetterInput,
	SendOnlineTestToUserInput,
	SendCaseStudyToUserInput,
	ShareProfileInput,
	ShortListCandidateInput,
	SkipUserToRoundInput,
	SubmitAssignmentInput,
	UndoRejectCandidateAPIInput,
	UpdateApplicationRoundsInput,
	UpdateAssignmentDeadline,
} from 'types';

const baseUrl = `${API_ENDPOINT}/application`;
export const getApplications = async (): Promise<GetMyApplicationsAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/all`);
	return response.data;
};
export const getAllApplications = async ({
	page,
	limit,
	showRecommendedOnly,
}: {
	page: string;
	limit: string;
	showRecommendedOnly?: string;
}): Promise<GetAllApplicationsAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/all-applications`, { params: { page, limit, showRecommendedOnly } });
	return response.data;
};

export const getOpenApplicationsCount = async (): Promise<GetOpenApplicationsAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/open-applications`);
	return response.data;
};

export const getMaxNumberOfRoundsForPosition = async (positionId: string): Promise<GetMaxNumberOfRoundsAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/max-number-of-rounds/${positionId}`);
	return response.data;
};

export const getUsersListForARound = async (
	positionId: string,
	roundNumber: number,
	roundType?: ApplicationRoundType,
	search?: string,
	active?: boolean,
): Promise<GetUsersListForARoundApiResponse> => {
	const response = await baseApi.get(`${baseUrl}/list-round-users/${positionId}`, {
		params: { roundNumber, roundType, search, active },
	});
	return response.data;
};

export const updateApplicationStatus = async (args: {
	id: string;
	status: ApplicationStatus;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/${args.id}`, { status: args.status });
	return response.data;
};

export const createApplication = async (args: {
	id: string;
	user: string;
	status: ApplicationStatus;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/create`, {
		createInfo: {
			position: args.id,
			user: args.user,
			status: args.status,
		},
	});
	return response.data;
};

export const createApplicationV2 = async (args: CreateApplicationInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/create`, {
		createInfo: { ...args.createInfo },
	});
	return response.data;
};

export const optOutOfApplicationProcess = async (args: CreateApplicationInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/opt-out`, {
		createInfo: { ...args.createInfo },
	});
	return response.data;
};

export const optOutOfPlacement = async (args: {
	reason: 'already_working' | 'got_placed';
	offerLetterUrl?: string;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/opt-out-of-placement`, {
		reason: args.reason,
		offerLetterUrl: args.offerLetterUrl,
	});
	return response.data;
};

export const requestOptingOutOfApplicationProcess = async (args: RequestForOptOutInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/request-for-opt-out`, {
		applicationId: args.applicationId,
		userId: args.userId,
		remarks: args.remarks,
	});
	return response.data;
};
export const acceptChangedApplicationTerms = async (args: AcceptChangedTermsInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/accept-changed-terms`, {
		applicationId: args.applicationId,
		positionId: args.positionId,
	});
	return response.data;
};
export const requestInterviewReschedule = async (args: RequestForOptOutInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/request-for-reschedule`, {
		applicationId: args.applicationId,
		userId: args.userId,
		remarks: args.remarks,
	});
	return response.data;
};

export const submitAssignment = async (args: SubmitAssignmentInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/submit-assignment`, {
		applicationId: args.applicationId,
		userId: args.userId,
		submissionLink: args.submissionLink,
		submittedLinks: args.submittedLinks,
		submittedFiles: args.submittedFiles,
		submissionType: args.submissionType,
	});
	return response.data;
};

export const offerLetterResponse = async (args: RespondToOfferLetterInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/userResponse/offer-letter`, {
		applicationId: args.applicationId,
		userId: args.userId,
		action: args.action,
		remarks: args.remarks,
	});
	return response.data;
};

export const shortlistUser = async (payload: ShortListCandidateInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/shortlist`, payload);
	return response.data;
};

export const sendAssignmentToUser = async (payload: SendAssignmentToUserInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/send-assignment`, payload);
	return response.data;
};

export const scheduleInterviewRoundForApplication = async (
	payload: ScheduleInterviewRoundInput,
): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/schedule-interview-round`, payload);
	return response.data;
};

export const rescheduleInterviewRoundForApplication = async (
	payload: ScheduleInterviewRoundInput,
): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/reschedule-interview`, payload);
	return response.data;
};

export const generateMeetingLink = async (payload: {
	positionId?: string;
	campusApplicationId?: string;
}): Promise<BaseAPIResponse & { meetingLink: string }> => {
	const response = await baseApi.patch(`${baseUrl}/generate-meeting-link`, payload);
	return response.data;
};

export const moveToNextRound = async (payload: MoveUserToNextRoundInput): Promise<MoveUserToNextRoundAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/move-to-next-round`, payload);
	return response.data;
};

export const skipToRound = async (payload: SkipUserToRoundInput): Promise<MoveUserToNextRoundAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/skip-to-round`, payload);
	return response.data;
};

export const updateNoOfRounds = async (payload: UpdateApplicationRoundsInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/update-application-rounds`, payload);
	return response.data;
};

export const sendOfferLetter = async (payload: SendOfferLetterInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/send-offer-letter`, payload);
	return response.data;
};

export const rejectCandidate = async (payload: RejectCandidateInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/reject-candidate`, payload);
	return response.data;
};

export const screenRejectCandidate = async (payload: ScreenRejectCandidateInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/screen-reject-candidate`, payload);
	return response.data;
};

export const shareProfiles = async (payload: ShareProfileInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/share-profile`, payload);
	return response.data;
};

export const bulkAction = async (payload: BulkActionByRecruiterInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/bulk-action`, payload);
	return response.data;
};

export const sendMessageToJobseeker = async (payload: SendMessageToJobseekerInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/send-message-to-jobseeker`, payload);
	return response.data;
};

export const sendAssignmentToJobseeker = async (payload: SendAssignmentToUserInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/send-assignment`, payload);
	return response.data;
};

export const sendOnlineTestToJobseeker = async (payload: SendOnlineTestToUserInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/send-online-test`, payload);
	return response.data;
};

export const sendCaseStudyToJobseeker = async (payload: SendCaseStudyToUserInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/send-case-study`, payload);
	return response.data;
};

export const submitOnlineTest = async (payload: { applicationId: string; submissionLink: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/submit-online-test`, payload);
	return response.data;
};

export const updateAssignmentToJobseeker = async (payload: UpdateAssignmentDeadline): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/update-assignment-deadline`, payload);
	return response.data;
};

export const shareJobseekerProfile = async (payload: ShareProfileInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/share-profile`, payload);
	return response.data;
};
export const rejectAction = async (payload: RejectActionForApplicationInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/reject-action`, payload);
	return response.data;
};
export const approveAction = async (payload: ApproveActionForApplicationInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/approve-action`, payload);
	return response.data;
};
export const cancelInterviewRound = async (payload: CancelInterviewRoundInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/cancel-interview-round`, payload);
	return response.data;
};
export const assignBehaviouralFlag = async (payload: AssignBehaviouralFlagInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/assign-behavioural-flag`, payload);
	return response.data;
};

export const revokeCandidateOffer = async (payload: UndoRejectCandidateAPIInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/revoke-offer`, payload);
	return response.data;
};

export const undoRejectedCandidate = async (payload: UndoRejectCandidateAPIInput): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${baseUrl}/undo-rejected-candidate`, payload);
	return response.data;
};

export const getUserDashboard = async (params?: {
	fromDate?: string;
	toDate?: string;
	search?: string;
	page?: number;
	limit?: number;
}): Promise<BaseAPIResponse & {
	data: {
		positionsRecommendedPushed: number;
		applied: number;
		appliedButNotShortlisted: number;
		processOngoing: number;
		processClosed: number;
		opportunities: {
			assignment: number;
			onlineTest: number;
			interview: number;
		};
		rejected: Array<{
			companyId: string;
			companyName: string;
			rejectedCount: number;
			positions: Array<{
				positionId: string;
				positionName: string;
				feedback?: string;
			}>;
		}>;
		rejectedMeta: {
			total: number;
			page: number;
			limit: number;
			totalPages: number;
		};
	};
}> => {
	// Add a cache buster and explicit no-cache header to avoid browser reusing stale 304 bodies across pages
	const response = await baseApi.get(`${baseUrl}/user-dashboard`, {
		params: { ...params, _ts: Date.now() },
		headers: { 'Cache-Control': 'no-cache' },
	});
	return response.data;
};
